<template>
  <h3>Contact Details</h3>
  <template v-if="user">
    <email-change-confirmation-box v-if="user?.unconfirmedEmailAddress" :user="user" />
    <edit-user-details
      v-model:surname="userDetails.surname"
      v-model:forename="userDetails.forename"
      v-model:email="userDetails.emailAddress"
      v-model:bank-holiday="userDetails.bankHolidays"
      :unconfirmed-email="user?.unconfirmedEmailAddress"
      :validation-errors="validationErrors"
      :bank-holidays-enabled="bankHolidaysEnabled"
      saml-warning-text="Your name or email address may be overwritten / automatically set by the identity provider"
    />

    <template v-if="additionalEmailsEnabled">
      <h3>Additional Emails</h3>
      <additional-emails
        v-model:email-addresses="userDetails.additionalEmails"
        v-model:default-internal="userDetails.defaultInternal"
        v-model:default-external="userDetails.defaultExternal"
        :validation-errors="validationErrors"
      />
    </template>

    <atom-button label="Save" type="primary" :disabled="saveDisabled" @click.prevent="saveUser"/>
  </template>
</template>

<script lang="ts">
import type { JsonUserAccount, UserDetails } from "authentication/js/api/user-account.api";
import type { AjaxError } from "core/js/spring-ajax";
import type { UserDetailValidationErrors } from "roles/components/user/EditUserDetails.vue";
import { useVuelidate } from "@vuelidate/core";
import { email, maxLength, required, requiredIf } from "@vuelidate/validators";
import { useUserAccountsStore } from "authentication/authentication.globals";
import AdditionalEmails from "authentication/components/mydetails/AdditionalEmails.vue";
import EmailChangeConfirmationBox from "authentication/components/mydetails/EmailChangeConfirmationBox.vue";
import { toJsonUserAccount } from "authentication/js/api/user-account.api";
import { useConfigOptionStore, useWhitelistedEmailDomainStore } from "control-panel/control-panel.globals";
import AtomButton from "core/components/atom/button/AtomButton.vue";
import { showChangesSavedFlash, showValidationFlash } from "core/js/flashmessage/flash-message";
import { getFirstValidationError } from "core/js/validation/validation-messages";
import { useCurrentSessionStore } from "core/store/current-session.store";
import { template } from "lodash";
import EditUserDetails from "roles/components/user/EditUserDetails.vue";
import { defineComponent } from "vue";

export default defineComponent({
  components: {
    EmailChangeConfirmationBox,
    AdditionalEmails,
    AtomButton,
    EditUserDetails,
  },
  setup() {
    return {
      configOptionStore: useConfigOptionStore(),
      emailDomainStore: useWhitelistedEmailDomainStore(),
      userAccountsStore: useUserAccountsStore(),
      sessionStore: useCurrentSessionStore(),
      v$: useVuelidate(),
    };
  },
  data(): {
    userDetails: UserDetails,
    validationErrors: UserDetailValidationErrors,
    saveDisabled: boolean,
  } {
    return {
      userDetails: {
        forename: "",
        surname: "",
        emailAddress: "",
        bankHolidays: undefined,
        additionalEmails: [],
        defaultInternal: "",
        defaultExternal: "",
      },
      validationErrors: {
        forename: "",
        surname: "",
        emailAddress: "",
        unconfirmedEmailAddress: "",
        additionalEmails: [],
        defaultInternal: "",
        defaultExternal: "",
      },
      saveDisabled: false,
    };
  },
  validations() {
    return {
      userDetails: {
        emailAddress: { required, email, maxLength: maxLength(140) },
        forename: { required, maxLength: maxLength(180) },
        surname: { required, maxLength: maxLength(140) },
        defaultInternal: { required: requiredIf(() => this.additionalEmailsEnabled && this.userDetails?.additionalEmails.length > 0) },
        defaultExternal: { required: requiredIf(() => this.additionalEmailsEnabled && this.userDetails?.additionalEmails.length > 0) },
      },
    };
  },
  computed: {
    userId(): number | undefined {
      return this.sessionStore.getUserId;
    },
    user(): JsonUserAccount | undefined {
      if (this.userId === undefined) {
        return undefined;
      }
      return this.userAccountsStore.getUser(this.userId);
    },
    bankHolidaysEnabled(): boolean {
      return this.configOptionStore.isConfigOptionEnabled("BANK_HOLIDAY_LOCATIONS");
    },
    additionalEmailsEnabled(): boolean {
      return this.configOptionStore.isConfigOptionEnabled("ADDITIONAL_EMAILS");
    },
  },
  async created() {
    await Promise.all([
      this.configOptionStore.isReady(),
      this.emailDomainStore.isReady(),
      this.userAccountsStore.isReady(),
      this.sessionStore.isReady(),
    ]);
    this.setUserDetailsFromStore();
  },
  methods: {
    setUserDetailsFromStore() {
      if (this.user) {
        this.userDetails = {
          forename: this.user.forename,
          surname: this.user.surname,
          emailAddress: this.user.emailAddress,
          bankHolidays: this.user.bankHolidays,
          additionalEmails: this.user.additionalEmails.map(email => ({ emailAddress: email.emailAddress, status: email.status })),
          defaultInternal: this.user.additionalEmails.find(email => email.defaultInternal)?.emailAddress,
          defaultExternal: this.user.additionalEmails.find(email => email.defaultExternal)?.emailAddress,
        };
      }
    },
    async saveUser() {
      this.resetValidationErrors();

      if (!await this.validateUserDetails()) {
        showValidationFlash();
        return;
      }

      this.saveDisabled = true;

      try {
        await this.userAccountsStore.updateUserAccount(toJsonUserAccount(this.user!, this.userDetails));
        this.setUserDetailsFromStore();
        showChangesSavedFlash();
      } catch (error: unknown) {
        const ajaxError = error as AjaxError;
        if (ajaxError.status === 422) {
          this.validationErrors = ajaxError.responseJSON;
          showValidationFlash();
        } else {
          throw error;
        }
      } finally {
        this.saveDisabled = false;
      }
    },
    isUnique(array: string[], value: string) {
      return (array.filter(x => x === value).length <= 1);
    },
    async validateUserDetails() {
      // email has not changed or email address is whitelisted
      const emailDomainValid = this.user!.emailAddress === this.userDetails.emailAddress
        || this.emailDomainStore.isEmailWhitelisted(this.userDetails.emailAddress);
      const valid = await this.v$.$validate() && emailDomainValid;
      if (!valid) {
        this.validationErrors.forename = getFirstValidationError(this.v$.userDetails.forename, "Forename");
        this.validationErrors.surname = getFirstValidationError(this.v$.userDetails.surname, "Surname");
        this.validationErrors.emailAddress = getFirstValidationError(
          this.v$.userDetails.emailAddress,
          "Email",
          { required: template("Enter an Email Address") },
        );
        this.validationErrors.additionalEmails = this.getAdditionalEmailsValidationErrors();
        this.validationErrors.defaultInternal = getFirstValidationError(
          this.v$.userDetails.defaultInternal,
          "Default internal",
          { required: template("Select an email address as the default for internal emails") },
        );
        this.validationErrors.defaultExternal = getFirstValidationError(
          this.v$.userDetails.defaultExternal,
          "Default external",
          { required: template("Select an email address as the default for external emails") },
        );

        if (!this.validationErrors.emailAddress && !emailDomainValid) {
          this.validationErrors.emailAddress = "Email must be from a whitelisted domain";
        }
      }

      return valid;
    },
    getAdditionalEmailsValidationErrors(): string[] {
      const emails = this.userDetails.additionalEmails.map(additionalEmail => additionalEmail.emailAddress);

      return emails.map((email) => {
        if (!this.isUnique(emails, email)) {
          return "Email addresses must be unique";
        } else {
          return "";
        }
      });
    },
    resetValidationErrors() {
      this.validationErrors = {
        emailAddress: "",
        unconfirmedEmailAddress: "",
        forename: "",
        surname: "",
        additionalEmails: [],
        defaultInternal: "",
        defaultExternal: "",
      };
    },
  },
});
</script>
